import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableItem from "../../../components/TableItem";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import Alart from "../../../components/Alart";
import Ex_ItemForm from "./Ex_ItemForm";
import { getProductBarcodes } from "../../../services/Export"; // CHANGED
import ExportBulkUpdate from "./Ex_BulkUpdate";
import { getUserType, getUser } from "../../../Utils/Common";

// Import icons
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

const isSalesPerson = () => getUserType() === "SALES_PERSON";

const getAssignedWarehouseIds = () => {
  const user = getUser();
  return (user?.wareHouseList || []).map((w) => w.warehouseId);
};

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

let _exItemListState = null;

function Ex_Items() {
  const history = useHistory();

  // State management
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(() => _exItemListState?.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [openBulkUpdateForm, setOpenBulkUpdateForm] = useState(false);

  // Disable warning state
  const [openDisableWarning, setOpenDisableWarning] = useState(false);
  const [msgDisableWarning, setMsgDisableWarning] = useState({
    title: "Warning",
    content: "Confirm You Need To Disable This Product",
  });
  const [disableItemId, setDisableItemId] = useState(null);

  // Filter state - SIMPLIFIED for product barcodes
  const [filter, setFilter] = useState(() => _exItemListState?.filter ?? {
    _barcode: "",
    _product: "",
    _addedBy: "",
  });

  // Table columns configuration - UPDATED for product barcodes
  const columns = [
    { id: "barcode", label: "Barcode", minWidth: 100 },
    { id: "productName", label: "Product Name", minWidth: 200 },
    {
      id: "totalQty",
      label: isSalesPerson() ? "Total Qty (Assigned Warehouses)" : "Total Qty (All Warehouses)",
      minWidth: 150,
      format: (value) => (
        <Chip
          label={value}
          color={value > 0 ? "success" : "error"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: "warehouseCount",
      label: "Warehouses",
      minWidth: 120,
      format: (value) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <WarehouseIcon sx={{ fontSize: 16, mr: 1, color: "primary.main" }} />
          {value}
        </Box>
      ),
    },
    { id: "addedBy", label: "Added By", minWidth: 120 },
    { id: "action", label: "Actions", minWidth: 150 },
  ];

  // Create data for table
  function createData(
    barcode,
    productName,
    totalQty,
    warehouseCount,
    addedBy,
    barcodeId,
  ) {
    return {
      barcode,
      productName,
      totalQty,
      warehouseCount,
      addedBy,
      action: barcodeId,
      barcodeId,
    };
  }

  // Initial data load
  useEffect(() => {
    _exItemListState = null;
    fetchData();
  }, []);

  // Fetch data from API - UPDATED to use getProductBarcodes
  const fetchData = async () => {
    setLoading(true);
    try {
      let result = await getProductBarcodes(page, rowsPerPage, filter);
      let productBarcodes = result.content;

      // Set total pages and rows for pagination
      setTotalPages(result.totalPages);
      setTotalRows(result.totalElements);

      const assignedWarehouseIds = isSalesPerson() ? getAssignedWarehouseIds() : null;

      const newSet = [];
      if (productBarcodes) {
        for (let x = 0; x < productBarcodes?.length; x++) {
          let totalQty, warehouseCount;
          if (assignedWarehouseIds) {
            const assignedStocks = (productBarcodes[x].warehouseStocks || []).filter(
              (s) => assignedWarehouseIds.includes(s.warehouseId),
            );
            totalQty = assignedStocks.reduce((sum, s) => sum + (s.stockQty || 0), 0);
            warehouseCount = assignedStocks.length;
          } else {
            totalQty = productBarcodes[x].totalQtyAllWarehouses || 0;
            warehouseCount = productBarcodes[x].numberOfWarehouses || 0;
          }
          newSet.push(
            createData(
              productBarcodes[x].barcode,
              productBarcodes[x].productName,
              totalQty,
              warehouseCount,
              productBarcodes[x].addedBy,
              productBarcodes[x].barcodeId,
            ),
          );
        }
      }

      setRows(newSet);
    } catch (error) {
      console.error("Error fetching product barcodes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData();
  };

  // Handle action for table actions - UPDATED
  const handleAction = async (event, id) => {
    switch (event) {
      case "view":
        _exItemListState = { page, filter };
        history.push("/template/product/" + id);
        break;
      case "edit":
        history.push("/template/product_barcode_edit/" + id); // NEW route
        break;
      case "disable":
        disableProduct(id);
        break;
      case "materials":
        history.push("/template/product_materials/" + id); // NEW route
        break;
      default:
        return;
    }
  };

  // Handle disable product
  const disableProduct = async (id) => {
    setDisableItemId(id);
    setOpenDisableWarning(true);
  };

  // Disable warning action buttons
  function DisableWarningActionButtons() {
    return (
      <>
        <ActionButton
          variant="contained"
          color="error"
          onClick={async () => {
            // await DiactivateItemById(disableItemId);
            setOpenDisableWarning(false);
            await fetchData();
          }}
        >
          Disable
        </ActionButton>
        <ActionButton
          variant="outlined"
          onClick={() => {
            setOpenDisableWarning(false);
          }}
        >
          Cancel
        </ActionButton>
      </>
    );
  }

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _barcode: "",
      _product: "",
      _addedBy: "",
    });

    fetchData();
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <>
      <Alart
        open={openDisableWarning}
        message={msgDisableWarning}
        actions={<DisableWarningActionButtons />}
      />
      <StyledPaper elevation={0}>
        {openForm ? (
          <Ex_ItemForm setOpenForm={setOpenForm} />
        ) : openBulkUpdateForm ? (
          <ExportBulkUpdate setOpenBulkUpdateForm={setOpenBulkUpdateForm} />
        ) : (
          <>
            <HeaderCard>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <InventoryIcon fontSize="large" />
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" fontWeight="bold">
                    Products (Master List)
                  </Typography>
                  <Typography variant="body2">
                    View and manage products across all warehouses
                  </Typography>
                </Grid>
                <Grid item>
                  <RoleBasedRender
                    allowedRoles={["PRODUCT_CREATE", "ROLE_ADMIN"]}
                  >
                    <ActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenForm(true)}
                    >
                      Add Product
                    </ActionButton>
                  </RoleBasedRender>
                </Grid>
                <Grid item>
                  <RoleBasedRender
                    allowedRoles={["PRODUCT_CREATE", "ROLE_ADMIN"]}
                  >
                    <ActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenBulkUpdateForm(true)}
                    >
                      Bulk Import
                    </ActionButton>
                  </RoleBasedRender>
                </Grid>
              </Grid>
            </HeaderCard>

            <Box
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <TextField
                placeholder="Search by product name or barcode..."
                variant="outlined"
                size="small"
                sx={{ width: "40%" }}
                value={filter._product}
                onChange={(e) => {
                  handleFilterChange("_product", e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "action.active", mr: 1 }} />
                  ),
                }}
              />
              <Stack direction="row" spacing={2}>
                <Tooltip title="Toggle Filters">
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon />}
                    onClick={toggleFilters}
                  >
                    Filters
                  </ActionButton>
                </Tooltip>
                <Chip
                  label={`${totalRows} Products`}
                  variant="outlined"
                  color="primary"
                />
              </Stack>
            </Box>

            {showFilters && (
              <FilterContainer>
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Advanced Filters
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputField
                      name="_barcode"
                      value={filter._barcode}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Barcode"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputField
                      name="_addedBy"
                      value={filter._addedBy}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Added By"
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Stack direction="row" spacing={2}>
                    <Tooltip title="Reset Filters">
                      <ActionButton
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={resetFilters}
                      >
                        Reset
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Apply Filters">
                      <ActionButton
                        variant="contained"
                        color="primary"
                        onClick={fetchData}
                      >
                        Apply Filters
                      </ActionButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </FilterContainer>
            )}

            <Divider sx={{ my: 2 }} />

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 5 }}>
                <Typography variant="body1" color="textSecondary">
                  No products found. Try adjusting your filters or add a new
                  product.
                </Typography>
              </Box>
            ) : (
              <TableItem
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={false}
                totalPages={totalPages}
                handleAction={handleAction}
                showActions={{
                  view: "View product details",
                  edit: "Edit product",
                  disable: "Deactivate product",
                  materials: "Manage materials (BOM)",
                }}
                actionRoles={{
                  view: ["PRODUCT_VIEW", "ROLE_ADMIN"],
                  edit: ["ROLE_ADMIN"],
                  disable: ["ROLE_ADMIN"],
                  materials: ["PRODUCT_UPDATE", "ROLE_ADMIN"],
                }}
                isProduction={false}
                tableProps={{
                  sx: {
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: alpha("#000", 0.04),
                    },
                  },
                }}
              />
            )}

            {totalPages > 1 && (
              <PaginationContainer>
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  onChange={handleChange}
                  size="medium"
                />
              </PaginationContainer>
            )}
          </>
        )}
      </StyledPaper>
    </>
  );
}

export default Ex_Items;
